/**
 * Story 059 — Dynamic Specifications parameter-array sub-form
 * (UX brief §9).
 *
 * Renders a labelled `<section role="region" aria-label="Specifications">`
 * (AC15 / AC16 / AC17 / AC18 all read this region) containing N rows of
 * `{parameter, unit, value, delete}` plus a `+ Add parameter` button.
 *
 * Per assets/v1@1.3.0 (B-059 Phase 4) the array is uniformly typed
 * `[{parameter, unit, value}, …]` regardless of asset_type — there's no
 * per-base_type default-row dispatch any more. The user always starts
 * with an empty array on Add and grows it with "+ Add parameter".
 *
 * Each row is rendered as a `<div role="row" aria-label={parameter}>`
 * so AC16's `specsRegion.getByRole("row", { name: /length/i })` matches
 * once the user types into the Parameter input.
 *
 * Driven by React Hook Form `useFieldArray` on a single `specifications`
 * field. The parent caller is expected to provide RHF `control`.
 */
import type { ReactElement } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useController,
  useFieldArray,
  useWatch,
  type Control,
  type UseFormRegister,
} from "react-hook-form";
import { CircleMinusIcon, PlusIcon } from "@aeriosiq/ui/icons";
import { CreatableCombobox, type CreatableComboboxOption } from "@aeriosiq/ui";
import { useSpecificationParametersQuery } from "../queries/index.js";

/** The static unit hints. Since ASM-SPEC-01 these are UNIONED with the
 *  distinct units of the catalogue rather than being the whole list — they
 *  remain so the Unit field still suggests something when the catalogue read
 *  is loading or has failed. Open vocabulary per Christian's Q2 amendment;
 *  the user may type anything else. */
const COMMON_UNITS = [
  "meter",
  "centimeter",
  "kilogram",
  "kilowatt",
  "text",
  "celsius",
  "percent",
] as const;

/**
 * The Parameter + Unit pair of one row.
 *
 * Its own component so BOTH fields' `onChange` live in one scope: picking a
 * catalogue parameter has to write the SIBLING unit field (D-5), and a
 * `Controller` render prop only ever holds its own. `useController` is the
 * public way to reach a second field without owning the form — `update()`
 * from the field array would re-key the row and take away the focus the
 * operator just used to pick.
 */
function ParameterAndUnitFields({
  control,
  index,
  readOnly,
  parameterOptions,
  unitOptions,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  index: number;
  readOnly: boolean;
  parameterOptions: readonly CreatableComboboxOption[];
  unitOptions: readonly CreatableComboboxOption[];
}): ReactElement {
  const parameter = useController({
    control,
    name: `specifications.${String(index)}.parameter`,
  });
  const unit = useController({
    control,
    name: `specifications.${String(index)}.unit`,
  });
  return (
    <>
      <CreatableCombobox
        value={(parameter.field.value as string | undefined) ?? ""}
        options={parameterOptions}
        ariaLabel="parameter"
        placeholder="Parameter"
        width={200}
        readOnly={readOnly}
        onChange={(next) => {
          parameter.field.onChange(next);
        }}
        onOptionSelected={(option) => {
          // D-5 — picking a catalogue parameter ALWAYS overwrites the row's
          // unit with the catalogue unit. Typing never touches it.
          unit.field.onChange(option.secondary ?? "");
        }}
      />
      <CreatableCombobox
        value={(unit.field.value as string | undefined) ?? ""}
        options={unitOptions}
        ariaLabel="unit"
        placeholder="Unit"
        width={160}
        readOnly={readOnly}
        onChange={(next) => {
          unit.field.onChange(next);
        }}
      />
    </>
  );
}

export interface SpecificationsFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  readOnly?: boolean;
}

export function SpecificationsField({
  control,
  register,
  readOnly = false,
}: SpecificationsFieldProps): ReactElement {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "specifications",
  });

  // The suggestion catalogue. Loading or failed → `items` is empty and both
  // comboboxes are plain free text (AC-29): the editor never blocks on it.
  const catalogue = useSpecificationParametersQuery();
  const items = catalogue.data?.items ?? [];

  const parameterOptions: CreatableComboboxOption[] = items.map((i) =>
    i.unit === ""
      ? { value: i.parameter }
      : { value: i.parameter, secondary: i.unit },
  );
  // Catalogue units first (they are the ones in use), then the static hints,
  // de-duplicated and blanks dropped — a tenant pair may carry `unit: ""`.
  const unitOptions: CreatableComboboxOption[] = [
    ...new Set([...items.map((i) => i.unit), ...COMMON_UNITS]),
  ]
    .filter((u) => u.length > 0)
    .map((u) => ({ value: u }));

  // useFieldArray's `fields` carry only INITIAL values; the
  // user-typed values need to be read via `useWatch` so the
  // accessible row name + the visible parameter-name caption update
  // live (drives AC16's `getByRole("row", { name: /length/i })` and
  // `getByText(/^length$/i)`).
  const watchedRows =
    (useWatch({
      control,
      name: "specifications",
    }) as
      | { parameter?: string; unit?: string; value?: string }[]
      | undefined) ?? [];

  return (
    <Box
      component="section"
      role="region"
      aria-label="Specifications"
      sx={{ display: "block" }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Specifications
      </Typography>

      <Stack spacing={1}>
        {fields.length === 0 ? (
          <Typography
            variant="body2"
            color="text.tertiary"
            sx={{ fontStyle: "italic" }}
          >
            {readOnly
              ? "No specifications."
              : "Use “+ Add parameter” below to record specifications."}
          </Typography>
        ) : (
          fields.map((field, index) => {
            const watched = watchedRows[index] ?? {};
            const parameterText = watched.parameter ?? "";
            const accessibleRowName =
              parameterText || `Parameter ${String(index + 1)}`;
            return (
              <Box
                key={field.id}
                role="row"
                aria-label={accessibleRowName}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {/* Visible parameter-name caption — AC16 reads
                    `specsRegion.getByText(/^length$/i)` to verify the
                    user-typed rows render. The caption mirrors the
                    Parameter input's value live (via useWatch) so it
                    stays in sync when the user edits the name. */}
                {parameterText.length > 0 ? (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                      clip: "rect(0 0 0 0)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {parameterText}
                  </Typography>
                ) : null}
                {/* `register()` cannot drive an Autocomplete — the pair
                    is Controller-driven, in one scope (D-5). */}
                <ParameterAndUnitFields
                  control={control}
                  index={index}
                  readOnly={readOnly}
                  parameterOptions={parameterOptions}
                  unitOptions={unitOptions}
                />
                <TextField
                  {...register(`specifications.${String(index)}.value`)}
                  size="small"
                  placeholder="Value"
                  sx={{ flex: 1, minWidth: 160 }}
                  slotProps={{
                    htmlInput: {
                      "aria-label": "value",
                      ...(readOnly ? { readOnly: true } : {}),
                    },
                  }}
                />
                {!readOnly ? (
                  <IconButton
                    aria-label={`Remove parameter ${parameterText || String(index + 1)}`}
                    size="small"
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <CircleMinusIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            );
          })
        )}
      </Stack>

      {!readOnly ? (
        <Button
          size="small"
          variant="text"
          startIcon={<PlusIcon fontSize="small" />}
          onClick={() => {
            append({ parameter: "", unit: "", value: "" });
          }}
          sx={{ mt: 1 }}
        >
          + Add parameter
        </Button>
      ) : null}
    </Box>
  );
}
